import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import config from "../../../config";
import {
  useJsApiLoader,
  GoogleMap,
  Polygon,
  Circle,
  Marker,
  Polyline,
} from "@react-google-maps/api";
import { GoogleMapsOverlay } from "@deck.gl/google-maps";
import { HeatmapLayer as DeckHeatmapLayer } from "@deck.gl/aggregation-layers";
import { ZoneAreaTypes, ZoneTypes } from "../../../models/zones/ZoneModel";
import ReportsApi from "../../../api/reportsApi";
import {
  FormGroup,
  FormControlLabel,
  Switch,
  Typography,
  Paper,
  Grid,
  Button,
  LinearProgress,
} from "@material-ui/core";
import Slider from "@material-ui/core/Slider";
import "./Heatmap.scss";
import getShapeOptions from "../../../utils/getZoneShapeOptions";

const mapContainerStyle = {
  width: "100%",
  height: 700,
};

const mapLibraries = ["visualization", "geometry"];

// deck.gl heatmap colorRange format [r, g, b, [a]]
const startPointsColorRange = [
  [0, 255, 255, 0],
  [0, 255, 255, 255],
  [0, 191, 255, 255],
  [0, 127, 255, 255],
  [0, 63, 255, 255],
  [0, 0, 255, 255],
  [0, 0, 223, 255],
  [0, 0, 191, 255],
  [0, 0, 159, 255],
  [0, 0, 127, 255],
  [63, 0, 91, 255],
  [127, 0, 63, 255],
  [191, 0, 31, 255],
  [255, 0, 0, 255],
];

const finishPointsColorRange = [
  [102, 255, 0, 0],
  [102, 255, 0, 255],
  [147, 255, 0, 255],
  [193, 255, 0, 255],
  [238, 255, 0, 255],
  [244, 227, 0, 255],
  [249, 198, 0, 255],
  [255, 170, 0, 255],
  [255, 113, 0, 255],
  [255, 57, 0, 255],
  [255, 0, 0, 255],
];

const getMarkerIcon = (zoneIconUrl) => {
  return {
    url: zoneIconUrl,
    /* eslint-disable no-undef */
    scaledSize: new google.maps.Size(32, 32),
  };
};

const MapView = (props) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: config.GOOGLE_MAPS_API_KEY,
    libraries: mapLibraries,
  });

  if (!isLoaded) return null;

  const mapOptions = {
    streetViewControl: false,
    mapTypeControl: true,
    mapTypeControlOptions: {
      /* eslint-disable no-undef */
      position: google.maps.ControlPosition.TOP_RIGHT,
    },
    clickableIcons: false,
    scaleControl: true,
  };

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      zoom={props.initialZoom}
      center={props.initialLocation}
      options={mapOptions}
      onLoad={props.onMapLoad}
      onUnmount={props.onMapUnmount}
    >
      {props.showOperationsZone &&
        props.zones
          .filter(
            (zone) =>
              zone.type === ZoneTypes.operationsZone.value &&
              zone.areaType === ZoneAreaTypes.polygon,
          )
          .map((zone) => (
            <Polygon
              key={zone.id}
              path={zone.area}
              options={getShapeOptions(zone)}
            />
          ))}
      {props.showOperationsZone &&
        props.zones
          .filter(
            (zone) =>
              zone.type === ZoneTypes.operationsZone.value &&
              zone.areaType === ZoneAreaTypes.circle,
          )
          .map((zone) => (
            <Circle
              key={zone.id}
              center={zone.area.center}
              radius={zone.area.radius}
              options={getShapeOptions(zone)}
            />
          ))}
      {props.showZones &&
        props.zones
          .filter(
            (zone) =>
              zone.type !== ZoneTypes.operationsZone.value &&
              zone.areaType === ZoneAreaTypes.polygon,
          )
          .map((zone) => (
            <Polygon
              key={zone.id}
              zIndex={0}
              path={zone.area}
              options={getShapeOptions(zone)}
            />
          ))}
      {props.showZones &&
        props.zones
          .filter(
            (zone) =>
              zone.type !== ZoneTypes.operationsZone.value &&
              zone.areaType === ZoneAreaTypes.circle,
          )
          .map((zone) => (
            <Circle
              key={zone.id}
              center={zone.area.center}
              radius={zone.area.radius}
              options={getShapeOptions(zone)}
            />
          ))}
      {props.showZones &&
        props.zones
          .filter((z) => z.marker)
          .map((zone) => (
            <Marker
              key={`marker${zone.id}`}
              icon={zone.marker.icon ? getMarkerIcon(zone.marker.icon) : null}
              position={zone.marker}
            />
          ))}
      {props.showConnections &&
        props.heatmapData.map((row, i) => (
          <Polyline
            key={`line${i}`}
            geodesic={true}
            options={{ strokeColor: "#4d4d4d", strokeWeight: 0.5 }}
            path={[
              { lat: row[0], lng: row[1] },
              { lat: row[2], lng: row[3] },
            ]}
          />
        ))}
    </GoogleMap>
  );
};

MapView.propTypes = {
  initialZoom: PropTypes.number.isRequired,
  initialLocation: PropTypes.object.isRequired,
  onMapLoad: PropTypes.func.isRequired,
  onMapUnmount: PropTypes.func.isRequired,
  heatmapData: PropTypes.array.isRequired,
  heatmapTracesData: PropTypes.array,
  showStartLocations: PropTypes.bool,
  showFinishLocations: PropTypes.bool,
  showConnections: PropTypes.bool.isRequired,
  showOperationsZone: PropTypes.bool.isRequired,
  showZones: PropTypes.bool.isRequired,
  showTraces: PropTypes.bool,
  maxIntensity: PropTypes.number,
  zones: PropTypes.array.isRequired,
};

class Heatmap extends Component {
  constructor(props) {
    super(props);
    this.map = null;
    this.deckOverlay = null;
    this.state = {
      heatmapData: [],
      heatmapTracesData: [],
      showStartLocations: true,
      showFinishLocations: false,
      showOperationsZone: true,
      showZones: false,
      showTraces: false,
      showConnections: false,
      maxIntensity: null,
      loading: true,
      loadingTrace: true,
    };
    this.handleRefreshData = this.handleRefreshData.bind(this);
    this.handleRefreshTracesData = this.handleRefreshTracesData.bind(this);
    this.handleChangeSetting = this.handleChangeSetting.bind(this);
    this.handleChangeIntensity = this.handleChangeIntensity.bind(this);
    this.handleMapLoad = this.handleMapLoad.bind(this);
    this.handleMapUnmount = this.handleMapUnmount.bind(this);
    this.updateDeckOverlay = this.updateDeckOverlay.bind(this);
  }

  componentDidMount() {
    this.handleRefreshData(this.props.dataFilter);
    this.handleRefreshTracesData(this.props.dataFilter);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.handleRefreshData(nextProps.dataFilter);
    this.handleRefreshTracesData(nextProps.dataFilter);
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.heatmapData !== this.state.heatmapData ||
      prevState.heatmapTracesData !== this.state.heatmapTracesData ||
      prevState.showStartLocations !== this.state.showStartLocations ||
      prevState.showFinishLocations !== this.state.showFinishLocations ||
      prevState.showTraces !== this.state.showTraces ||
      prevState.maxIntensity !== this.state.maxIntensity
    ) {
      this.updateDeckOverlay();
    }
  }

  componentWillUnmount() {
    this.handleMapUnmount();
  }

  handleMapLoad(map) {
    this.map = map;
    this.deckOverlay = new GoogleMapsOverlay({
      layers: [],
    });

    this.deckOverlay.setMap(map);
    this.updateDeckOverlay();
  }

  handleMapUnmount() {
    if (this.deckOverlay) {
      this.deckOverlay.setMap(null);
      this.deckOverlay.finalize();
      this.deckOverlay = null;
    }

    this.map = null;
  }

  updateDeckOverlay() {
    if (!this.deckOverlay) return;

    const {
      heatmapData,
      heatmapTracesData,
      showStartLocations,
      showFinishLocations,
      showTraces,
      maxIntensity,
    } = this.state;

    const colorDomain = maxIntensity ? [0, maxIntensity] : null;

    const layers = [
      showStartLocations &&
        new DeckHeatmapLayer({
          id: "start-locations-heatmap",
          data: heatmapData,
          getPosition: (row) => [row[1], row[0]],
          getWeight: () => 1,
          colorRange: startPointsColorRange,
          colorDomain,
          radiusPixels: 25,
          aggregation: "SUM",
        }),

      showFinishLocations &&
        new DeckHeatmapLayer({
          id: "finish-locations-heatmap",
          data: heatmapData,
          getPosition: (row) => [row[3], row[2]],
          getWeight: () => 1,
          colorRange: finishPointsColorRange,
          colorDomain,
          radiusPixels: 25,
          aggregation: "SUM",
        }),

      showTraces &&
        new DeckHeatmapLayer({
          id: "traces-heatmap",
          data: heatmapTracesData,
          getPosition: (row) => [row[1], row[0]],
          getWeight: () => 1,
          colorRange: finishPointsColorRange,
          colorDomain,
          radiusPixels: 25,
          aggregation: "SUM",
        }),
    ].filter(Boolean);

    this.deckOverlay.setProps({ layers });
  }

  handleRefreshData(dataFilter) {
    this.setState({ loading: true });
    ReportsApi.getHeatmapData(dataFilter)
      .then((response) => {
        this.setState({ heatmapData: response.data });
      })
      .finally(() => this.setState({ loading: false }));
  }

  handleRefreshTracesData(dataFilter) {
    this.setState({ loadingTrace: true });
    ReportsApi.getHeatmapTracesData(dataFilter)
      .then((response) => {
        this.setState({ heatmapTracesData: response.data });
      })
      .finally(() => this.setState({ loadingTrace: false }));
  }

  handleChangeSetting(name) {
    return (event) => {
      this.setState({ ...this.state, [name]: event.target.checked });
    };
  }

  handleChangeIntensity(event, value) {
    this.setState({ maxIntensity: value });
  }

  render() {
    const { initialLocation, zones } = this.props;

    if (this.state.loading || this.state.loadingTrace)
      return (
        <Paper style={{ padding: 16 }}>
          <Typography align="center" variant="subtitle1">
            Wczytywanie danych
          </Typography>
          <LinearProgress />
        </Paper>
      );

    return (
      <Paper>
        <MapView
          onMapLoad={this.handleMapLoad}
          onMapUnmount={this.handleMapUnmount}
          initialZoom={12}
          initialLocation={initialLocation}
          heatmapData={this.state.heatmapData}
          heatmapTracesData={this.state.heatmapTracesData}
          showStartLocations={this.state.showStartLocations}
          showFinishLocations={this.state.showFinishLocations}
          showConnections={this.state.showConnections}
          showOperationsZone={this.state.showOperationsZone}
          showZones={this.state.showZones}
          showTraces={this.state.showTraces}
          maxIntensity={this.state.maxIntensity}
          zones={zones}
        />
        <div style={{ padding: 16 }}>
          <Typography variant="body2">
            Liczba wizualizowanych przejazdów: {this.state.heatmapData.length}
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={this.state.showStartLocations}
                  onChange={this.handleChangeSetting("showStartLocations")}
                  value="showStartLocations"
                />
              }
              label="Miejsca rozpoczęcia przejazdów"
            />
            {this.state.showStartLocations && (
              <div id="started-heatmap-legend" />
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={this.state.showFinishLocations}
                  onChange={this.handleChangeSetting("showFinishLocations")}
                  value="showFinishLocations"
                />
              }
              label="Miejsca zakończenia przejazdów"
            />
            {this.state.showTraces && <div id="finished-heatmap-legend" />}
            <FormControlLabel
              control={
                <Switch
                  checked={this.state.showTraces}
                  onChange={this.handleChangeSetting("showTraces")}
                  value="showTraces"
                />
              }
              label="Trasa"
            />
            {this.state.showTraces && <div id="trace-heatmap-legend" />}
            <FormControlLabel
              control={
                <Switch
                  checked={this.state.showConnections}
                  onChange={this.handleChangeSetting("showConnections")}
                  value="showConnections"
                />
              }
              label="Wyświetlaj połączenia"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={this.state.showOperationsZone}
                  onChange={this.handleChangeSetting("showOperationsZone")}
                  value="showOperationsZone"
                />
              }
              label="Wyświetlaj obszar działania"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={this.state.showZones}
                  onChange={this.handleChangeSetting("showZones")}
                  value="showZones"
                />
              }
              label="Wyświetlaj strefy"
            />
            <Typography variant="caption" id="slider" gutterBottom>
              Próg maksymalnej intensywności koloru - minimalna liczba
              przejazdów zaliczana jako maksymalny próg temperaturowy mapy
              ciepła:{" "}
              <strong>
                {this.state.maxIntensity ? this.state.maxIntensity : "automat"}
              </strong>
            </Typography>
            <Grid container spacing={2}>
              <Grid item>
                <Button onClick={() => this.setState({ maxIntensity: null })}>
                  Domyślne
                </Button>
              </Grid>
              <Grid item>
                <Typography>1</Typography>
              </Grid>
              <Grid item xs>
                <Slider
                  id="slider"
                  min={1}
                  marks
                  max={this.state.heatmapData.length || 100}
                  value={this.state.maxIntensity}
                  onChange={this.handleChangeIntensity}
                  aria-labelledby="continuous-slider"
                />
              </Grid>
              <Grid item>
                <Typography>{this.state.heatmapData.length}</Typography>
              </Grid>
            </Grid>
          </FormGroup>
        </div>
      </Paper>
    );
  }
}

Heatmap.propTypes = {
  dataFilter: PropTypes.array,
  zones: PropTypes.array.isRequired,
  initialLocation: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  return {
    zones: state.zones,
    initialLocation: state.configuration.initialLocation,
  };
}

export default connect(mapStateToProps)(Heatmap);